import React, { Component } from "react";
import { observer } from "mobx-react";
import { AxiosError, AxiosResponse } from "axios";
import { getRequest, postRequest } from "../../utils/httpRequest";
import { Dropdown, Form } from "semantic-ui-react";
import { notification } from "../../utils/helpers";
import { Redirect } from "react-router-dom";

interface State {
  firstName: string;
  lastName: string;
  selectedCountry: string;
  recieveNewsletter: boolean;
  redirectUrl: string;
  countries: any;
  loaded: boolean;
  submitting: boolean;
  showHelp: boolean;
}

class UserSetup extends Component<{}, State> {
  helpRef = React.createRef<HTMLDivElement>();

  constructor(props) {
    super(props);

    this.state = {
      firstName: "",
      lastName: "",
      selectedCountry: "",
      recieveNewsletter: true,
      redirectUrl: null,
      countries: [],
      loaded: false,
      submitting: false,
      showHelp: false,
    };
  }

  fetchCountries = () => {
    return getRequest("/api/countries.json").then(
      (response: AxiosResponse<any>) => {
        const countries = response.data.map((countryResource) => {
          return {
            text: countryResource.country,
            value: countryResource.code,
            flag: countryResource.code.toLowerCase(),
            key: countryResource.code,
          };
        });
        this.setState({ countries: countries, loaded: true });
      },
    );
  };

  componentDidMount() {
    document.addEventListener("mousedown", this.handleHelpOutsideClick);
    document.addEventListener("keydown", this.handleHelpKeydown);

    return getRequest("/api/current_user.json").then(
      (response: AxiosResponse<any>) => {
        this.setState({
          firstName: response.data.first_name,
          lastName: response.data.last_name,
          selectedCountry: response.data.country,
        });
        this.fetchCountries();
      },
    );
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleHelpOutsideClick);
    document.removeEventListener("keydown", this.handleHelpKeydown);
  }

  setAttribute = (e) => {
    e.preventDefault();
    let event_hash = {};
    event_hash[e.target.name] = e.target.value;
    this.setState(event_hash);
  };

  handleCountryChange = (event, data) => {
    this.setState({ selectedCountry: data.value });
  };

  handleNewsletter = (event) => {
    this.setState({ recieveNewsletter: !this.state.recieveNewsletter });
  };

  onSubmit = (e) => {
    e.preventDefault();

    const { firstName, lastName, selectedCountry, recieveNewsletter } =
      this.state;

    this.setState({ submitting: true });

    const userData = {
      user: {
        first_name: firstName ? firstName : "",
        last_name: lastName ? lastName : "",
        name: firstName && lastName ? `${firstName} ${lastName}` : "",
        country: selectedCountry,
        receive_newsletter: recieveNewsletter,
        name_required: true,
      },
    };

    return postRequest("/api/profile/setup.json", userData)
      .then((response: AxiosResponse<{ url: string; success: string }>) => {
        notification(response.data.success, "notification");
        (window as any).location = response.data.url;
      })
      .catch((error: AxiosError<{ error: string }>) => {
        notification(error.response.data.error, "error");
        this.setState({ submitting: false });
      });
  };

  toggleHelp = () => {
    this.setState({ showHelp: !this.state.showHelp });
  };

  handleHelpOutsideClick = (event: MouseEvent) => {
    if (
      this.state.showHelp &&
      this.helpRef.current &&
      !this.helpRef.current.contains(event.target as Node)
    ) {
      this.setState({ showHelp: false });
    }
  };

  handleHelpKeydown = (event: KeyboardEvent) => {
    if (this.state.showHelp && event.key === "Escape") {
      this.setState({ showHelp: false });
    }
  };

  renderBlob = (className: string) => (
    <div className={`auth-blob ${className}`} aria-hidden="true">
      <svg viewBox="0 0 200 250" xmlns="http://www.w3.org/2000/svg">
        <path
          fill="#2E2437"
          d="M175.67,131.46C185.77,91.05,149.4,97.11,129.19,85S113.86,55.87,78.68,52.66,20,70.8,14,101.15,14.24,173.8,60.49,184,165.56,171.88,175.67,131.46Z"
        />
        <path
          fill="#2E2437"
          d="M137.92,47.6c-5.66,4.09-16.39,18.52-7.47,26.74s24.3,5.46,31.37-7.73c6.33-11.78-1.85-18.29-7.66-20.88S141.17,45.31,137.92,47.6Z"
        />
      </svg>
    </div>
  );

  render() {
    const {
      loaded,
      countries,
      firstName,
      lastName,
      selectedCountry,
      recieveNewsletter,
      redirectUrl,
      submitting,
      showHelp,
    } = this.state;

    return (
      <div className="ui pages" id="user_setup">
        {!loaded && (
          <div className="ui active inverted dimmer">
            <div className="ui text loader">Loading</div>
          </div>
        )}

        {redirectUrl && <Redirect to={{ pathname: redirectUrl }} />}
        <a href="#profile-card" className="skip-link">
          Skip to the form
        </a>

        <div className="auth-page">
          {this.renderBlob("one")}
          {this.renderBlob("two")}

          <header className="auth-header">
            <a className="brand" href="/" aria-label="42courses home">
              <img
                src={require("../../../../assets/images/facelift/logos/dark_rectangle.png")}
                alt="42courses"
              />
            </a>

            <div className="help-wrap" ref={this.helpRef}>
              <button
                type="button"
                className="help-link"
                aria-expanded={showHelp}
                aria-controls="setup-help-pop"
                onClick={this.toggleHelp}
              >
                Need a hand?
              </button>
              {showHelp && (
                <div
                  className="help-pop"
                  id="setup-help-pop"
                  role="dialog"
                  aria-label="Need a hand?"
                >
                  <p>
                    If you are stuck or need a hand, reach out to us at{" "}
                    <a href="mailto:groundcontrol@42courses.com">
                      groundcontrol@42courses.com
                    </a>
                    .
                  </p>
                </div>
              )}
            </div>
          </header>

          <main className="auth-main">
            <section
              className="profile-card"
              id="profile-card"
              aria-labelledby="profile-title"
            >
              <div className="profile-head">
                <p className="profile-eyebrow">Almost there</p>
                <h1 className="profile-title" id="profile-title">
                  Nice to meet you.
                </h1>
                <p className="profile-sub">
                  A couple of quick details and you are in.
                </p>
              </div>

              <Form className="profile-form" onSubmit={this.onSubmit}>
                <div className="field-block">
                  <div className="field-row">
                    <div className="field-group">
                      <label className="field-label" htmlFor="first-name">
                        First name
                      </label>
                      <input
                        className="pill-input"
                        id="first-name"
                        type="text"
                        value={firstName}
                        onChange={this.setAttribute}
                        name="firstName"
                        autoComplete="given-name"
                        required
                      />
                    </div>
                    <div className="field-group">
                      <label className="field-label" htmlFor="last-name">
                        Last name
                      </label>
                      <input
                        className="pill-input"
                        id="last-name"
                        type="text"
                        value={lastName}
                        onChange={this.setAttribute}
                        name="lastName"
                        autoComplete="family-name"
                        required
                      />
                    </div>
                  </div>
                  <p className="help-text">
                    This is the name that goes on your course certificates, so
                    spell it the way you would like the world to see it.
                  </p>
                </div>

                <div className="field-block">
                  <div className="field-group">
                    <label className="field-label" htmlFor="country">
                      Country
                    </label>
                    <Dropdown
                      id="country"
                      className="country-dropdown"
                      fluid
                      search
                      selection
                      options={countries}
                      value={selectedCountry}
                      placeholder="Select country"
                      onChange={this.handleCountryChange}
                    />
                  </div>
                  <p className="help-text">
                    Pop yourself on the map and help your country climb the
                    leaderboard.
                  </p>
                </div>

                <label className="check-row" htmlFor="updates">
                  <input
                    type="checkbox"
                    id="updates"
                    name="updates"
                    onChange={this.handleNewsletter}
                    checked={recieveNewsletter}
                  />
                  <span className="check-box" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 12l5 5L20 7" />
                    </svg>
                  </span>
                  <span className="check-label">
                    Keep me posted about new courses. No nonsense, just the good
                    stuff.
                  </span>
                </label>

                <button
                  type="submit"
                  className={`profile-submit ${submitting ? "loading" : ""}`}
                  disabled={submitting}
                >
                  Continue
                </button>
              </Form>
            </section>
          </main>

          <footer className="auth-footer">
            Made with care in London. &nbsp;&middot;&nbsp;{" "}
            <a href="/">Back to 42courses</a>
          </footer>
        </div>
      </div>
    );
  }
}
export default observer(UserSetup);
